import { useMemo, useState } from "react";
import { Download, Receipt, Search } from "lucide-react";

import type { EstadoMovimiento, Factura, Movimiento } from "../api/tipos.ts";
import { usarDecidir, usarElegirCuenta, usarLibro, type Decision } from "../ganchos/datos.ts";
import { usarSesion } from "../sesion/ContextoSesion.tsx";
import { PERMISO } from "../sesion/permisos.ts";
import { esElMontoBuscado, fechaConHora, fechaCorta, fechaHora, haceRato, instanteDe, pesos, soloHora } from "../formato.ts";
import { ESTADO_MOVIMIENTO, ORIGEN } from "../vocabulario.ts";
import { AccionesDePago, Ejes, Lado } from "../componentes/Encuentro.tsx";
import { ejesDe, ladosDe } from "../componentes/ejes.ts";
import { avisoDeCorreccion } from "../componentes/correccion.ts";
import { ErrorConsulta, EsqueletoTabla } from "../componentes/Errores.tsx";
import { Aviso, Boton, Dialogo, Insignia, InsigniaDe, Seccion, Tarjeta, Vacio, cx } from "../componentes/ui.tsx";
import { CuentaDelPago } from "../componentes/CuentaDelPago.tsx";
import { cuentaCorta, enLaLista, identificadorDe, llaveDe, rotuloDeCuenta, sinCuenta } from "../componentes/cuenta-modelo.ts";

const ESTADOS: EstadoMovimiento[] = [
  "CONCILIADO",
  "IDENTIFICADO",
  "POR_CONFIRMAR",
  "CONFLICTO",
  "SIN_IDENTIFICAR",
  "DATO_INVALIDO",
];

/**
 * El libro completo: se busca, se audita, se explica — y desde el detalle
 * también se decide. Cada fila abre su trazabilidad, que es lo que convierte
 * "el sistema dijo" en "el sistema dijo esto, por esto, y lo confirmó fulano
 * tal día".
 *
 * Decidir aquí SÍ es distinto que en la bandeja, y el modal lo dice: allí solo
 * hay pagos que nadie ha resuelto; aquí la mayoría ya tiene dueño, y pulsar el
 * mismo botón puede significar reemplazar lo que decidió una compañera.
 */
export function Movimientos() {
  const { data: libro, isPending, error, refetch } = usarLibro();
  const { puedo } = usarSesion();
  const [busqueda, setBusqueda] = useState("");
  const [estado, setEstado] = useState<EstadoMovimiento | "todo">("todo");
  // «todas», «sin» (sin identificar) o la llave de una cuenta de la lista (SPEC-030 §7).
  const [cuenta, setCuenta] = useState("todas");
  const [abierto, setAbierto] = useState<Movimiento | null>(null);
  const lista = libro?.cuentasBancarias ?? [];


  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return (libro?.movimientos ?? [])
      .filter((m) => {
        if (estado !== "todo" && m.estado !== estado) return false;
        if (cuenta === "sin" && !sinCuenta(m)) return false;
        if (cuenta !== "todas" && cuenta !== "sin") {
          const c = enLaLista(m.cuentaBancaria, lista);
          if (!c || llaveDe(c) !== cuenta) return false;
        }
        if (!q) return true;
        // Un número busca el monto Y el texto: «1089» puede ser $1.089 o la
        // factura 1089 (SPEC-029 §8).
        if (esElMontoBuscado(q, m.montoCentavos)) return true;
        return [m.contraparte, m.cliente, m.factura, m.razon, m.llave]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      })
      // Del más nuevo al más viejo, igual que la bandeja: esta lista se consulta
      // para responder «¿entró ya el pago de hoy?», y con lo viejo arriba había
      // que bajar hasta el final para saberlo.
      //
      // Los movimientos anteriores a la migración 002 no tienen hora, así que
      // entre dos del mismo día el orden es el que traiga el libro. Ordenar es
      // de quien lista, pero inventar una hora para desempatar sería afirmar
      // algo que nadie sabe.
      .sort((a, b) => instanteDe(b.fecha) - instanteDe(a.fecha));
  }, [libro?.movimientos, busqueda, estado, cuenta, lista]);

  if (error) return <ErrorConsulta error={error} alReintentar={() => void refetch()} />;

  return (
    <Seccion
      titulo="Pagos"
      descripcion="Todo lo que entró al banco, con el porqué de cada estado."
      accion={
        puedo(PERMISO.EXPORTAR) && (
          <Boton
            variante="suave"
            tamano="sm"
            icono={Download}
            disabled={!filtrados.length}
            onClick={() => descargarCsv(filtrados)}
          >
            Exportar CSV
          </Boton>
        )
      }
    >
      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-56 flex-1">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tenue-2"
            aria-hidden
          />
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por monto, quién pagó, cliente o factura…"
            className="h-10 w-full rounded-xl border border-borde bg-superficie pr-3.5 pl-10 text-sm placeholder:text-tenue-2 focus:border-marca focus:ring-4 focus:ring-marca/12 focus:outline-none"
          />
        </div>
        <select
          value={estado}
          onChange={(e) => setEstado(e.target.value as EstadoMovimiento | "todo")}
          className="h-10 rounded-xl border border-borde bg-superficie px-3 text-sm focus:border-marca focus:ring-4 focus:ring-marca/12 focus:outline-none"
        >
          <option value="todo">Todos los estados</option>
          {ESTADOS.map((e) => (
            <option key={e} value={e}>
              {ESTADO_MOVIMIENTO[e].texto}
            </option>
          ))}
        </select>
        <select
          value={cuenta}
          onChange={(e) => setCuenta(e.target.value)}
          aria-label="Cuenta"
          className="h-10 rounded-xl border border-borde bg-superficie px-3 text-sm focus:border-marca focus:ring-4 focus:ring-marca/12 focus:outline-none"
        >
          <option value="todas">Todas las cuentas</option>
          <option value="sin">Cuenta sin identificar</option>
          {lista.map((c) => (
            <option key={llaveDe(c)} value={llaveDe(c)}>
              {c.nombre} ({identificadorDe(c)})
            </option>
          ))}
        </select>
      </div>

      {/* Montado aunque la lista se vacíe: al asignar, los pagos dejan de estar
          «sin identificar» y el «Deshacer» tiene que seguir a la vista. */}
      {cuenta === "sin" && puedo(PERMISO.DECIDIR) && <AsignarCuenta pagos={filtrados} />}

      <Tarjeta className="overflow-hidden">
        {isPending ? (
          <div className="p-4">
            <EsqueletoTabla />
          </div>
        ) : filtrados.length === 0 ? (
          <Vacio icono={Receipt} titulo="Sin movimientos que mostrar">
            {libro?.movimientos.length
              ? "Ninguno coincide con lo que buscas."
              : "Cuando llegue la primera alerta del banco, aparecerá aquí."}
          </Vacio>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-borde text-left text-xs tracking-wide text-tenue uppercase">
                  <th className="px-4 py-3 font-medium">Fecha</th>
                  <th className="px-4 py-3 text-right font-medium">Monto</th>
                  <th className="px-4 py-3 font-medium">Quién pagó</th>
                  <th className="px-4 py-3 font-medium">Cliente</th>
                  <th className="px-4 py-3 font-medium">Factura</th>
                  <th className="px-4 py-3 font-medium">Cuenta</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium">Origen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-borde">
                {filtrados.map((m) => (
                  <tr
                    key={m.clave}
                    tabIndex={0}
                    onClick={() => setAbierto(m)}
                    onKeyDown={(e) => e.key === "Enter" && setAbierto(m)}
                    className="cursor-pointer transition-colors hover:bg-superficie-2"
                  >
                    {/* La hora debajo del día: dos pagos del mismo monto el
                        mismo día solo se distinguen por ella. Los movimientos
                        cargados antes de la migración 002 no la traen, y
                        entonces no se inventa. */}
                    <td className="px-4 py-3 whitespace-nowrap text-tenue" title={fechaConHora(m.fecha)}>
                      {fechaCorta(m.fecha)}
                      {soloHora(m.fecha) && (
                        <span className="cifra block text-xs text-tenue-2">{soloHora(m.fecha)}</span>
                      )}
                    </td>
                    <td className="cifra px-4 py-3 text-right font-medium whitespace-nowrap">
                      {pesos(m.montoCentavos)}
                    </td>
                    {/* El `title` no sobra aunque la columna sea ancha: siempre
                        habrá una razón social más larga que cualquier límite, y
                        pasar el ratón es más barato que abrir el detalle solo
                        para leer un nombre. */}
                    {/* La sucursal va DEBAJO y no en columna propia: solo la
                        trae la consignación en corresponsal, así que una
                        columna estaría vacía en casi todas las filas. Y va
                        justo aquí porque es lo único que se sabe del origen
                        cuando no hay contraparte: sin ella, esas filas dicen
                        "—" y no se distinguen entre sí. */}
                    <td className="max-w-[22rem] px-4 py-3" title={m.contraparte || m.sucursal || undefined}>
                      <span className="block truncate">{m.contraparte || "—"}</span>
                      {m.sucursal && (
                        <span className="block truncate text-xs text-tenue-2" title={m.sucursal}>
                          {m.sucursal}
                        </span>
                      )}
                    </td>
                    <td className="max-w-[18rem] truncate px-4 py-3 text-tenue" title={m.cliente || undefined}>
                      {m.cliente || "—"}
                    </td>
                    {/* El CSV ya la exportaba y la tabla no la enseñaba: había
                        que abrir el detalle o descargar el archivo para ver
                        contra qué se aplicó un pago. `cifra` porque son números
                        y se leen en columna. */}
                    <td className="cifra max-w-[12rem] truncate px-4 py-3 text-tenue" title={m.factura || undefined}>
                      {m.factura || "—"}
                    </td>
                    {/* A qué cuenta del negocio entró (SPEC-030 §7): el nombre
                        corto, o resaltado cuando nadie lo sabe. */}
                    <td className="max-w-[12rem] px-4 py-3 whitespace-nowrap">
                      {cuentaCorta(m.cuentaBancaria) ? (
                        <span className="block truncate text-tenue" title={cuentaCorta(m.cuentaBancaria)}>
                          {cuentaCorta(m.cuentaBancaria)}
                        </span>
                      ) : (
                        <span className="rounded-full bg-aviso-suave px-2 py-0.5 text-xs font-medium text-aviso-tinta">
                          Sin identificar
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <InsigniaDe etiqueta={ESTADO_MOVIMIENTO[m.estado]} />
                    </td>
                    <td className="px-4 py-3">
                      <Insignia tono={ORIGEN[m.origen].tono} titulo={ORIGEN[m.origen].explica}>
                        {ORIGEN[m.origen].texto}
                      </Insignia>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Tarjeta>

      <Detalle
        movimiento={abierto}
        cartera={libro?.facturas ?? []}
        alCerrar={() => setAbierto(null)}
      />
    </Seccion>
  );
}

function Detalle({
  movimiento: m,
  cartera,
  alCerrar,
}: {
  movimiento: Movimiento | null;
  cartera: Factura[];
  alCerrar: () => void;
}) {
  const { puedo } = usarSesion();
  const decidir = usarDecidir();
  const { registro } = m ? ladosDe(m) : { registro: null };
  // `haceRato` devuelve "—" cuando no hay fecha, y «ya decidió este pago —.» no
  // es una frase. Sin `cuando`, mejor no decir cuándo.
  const aviso = m ? avisoDeCorreccion(m, m.cuando ? haceRato(m.cuando) : "") : null;

  // Se cierra al decidir. La lista de detrás se recalcula sola y el pago puede
  // cambiar de estado o de sitio; dejar el modal abierto mostrando lo de antes
  // sería enseñar un dato viejo justo después de cambiarlo.
  function alDecidir(d: Decision) {
    decidir.mutate(d, { onSuccess: alCerrar });
  }

  return (
    <Dialogo
      // Más ancho que el defecto: este diálogo ya no solo muestra: dentro caben
      // los ejes del encuentro y el formulario de etiquetar, y en `max-w-lg`
      // los botones se apilaban de a uno.
      ancho="max-w-2xl"
      abierto={!!m}
      alCerrar={alCerrar}
      titulo={m ? pesos(m.montoCentavos) : ""}
      descripcion={m ? `${m.contraparte || "el banco no dice quién"} · ${fechaHora(m.fecha)}` : ""}
    >
      {m && (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-2">
            <InsigniaDe etiqueta={ESTADO_MOVIMIENTO[m.estado]} />
            <InsigniaDe etiqueta={ORIGEN[m.origen]} />
            {m.claveEstable === false && (
              <Insignia tono="riesgo" titulo="Hay otro movimiento con la misma huella.">
                Identidad no distinguible
              </Insignia>
            )}
          </div>

          <div className="rounded-xl bg-superficie-2 p-4 text-sm leading-relaxed text-tenue">
            {m.razon}
          </div>

          {/*
            Los mismos ejes de la bandeja, no una tira de insignias distinta.
            Que la evidencia se lea igual aquí que donde se decidió es lo que
            permite volver meses después y entender por qué se decidió así.
          */}
          <EncuentroHistorico movimiento={m} />

          <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            <Dato termino="Cliente" valor={m.cliente} />
            <Dato termino="Factura" valor={m.factura} />
            <Dato termino="Llave del banco" valor={m.llave} />
            <Dato termino="Regla aplicada" valor={m.regla} />
            <Dato termino="Reconocido por" valor={m.identificador} />
            <Dato termino="Clave interna" valor={m.clave} mono />
          </dl>

          <CuentaDelPago movimiento={m} />

          {(m.quien || m.cuando) && (
            <div className="rounded-xl border border-humano/25 bg-humano-suave p-4 text-sm text-humano-tinta">
              <p className="font-medium">
                {m.quien ?? "Alguien"} lo decidió {haceRato(m.cuando)}
              </p>
              <p className="mt-0.5 opacity-80">{fechaHora(m.cuando)}</p>
              {m.nota && <p className="mt-2 italic opacity-90">«{m.nota}»</p>}
            </div>
          )}

          {/*
            La zona de decidir se sale de los márgenes del diálogo a propósito:
            no es un apartado más del detalle, es donde se actúa. El violeta es
            el mismo con el que la app marca «esto lo decidió una persona» —el
            recuadro de arriba lo usa—, así que el color dice de quién es esta
            franja en vez de solo separarla.

            Deliberadamente NO es el teal de la marca: la regla del encuentro es
            que el teal aparece solo donde el banco y tu registro se tocan, y
            gastarlo en decorar una franja lo vaciaría de significado.

            Va al final porque lo de arriba —quién decidió y cuándo— es lo que
            hay que leer ANTES de reemplazarlo.
          */}
          {puedo(PERMISO.DECIDIR) && (
            <div className="-mx-5 -mb-5 border-t-2 border-humano/30 bg-humano-suave/50 px-5 py-5">
              <AccionesDePago
                movimiento={m}
                registro={registro}
                cartera={cartera}
                decidiendo={decidir.isPending}
                alDecidir={alDecidir}
                aviso={
                  aviso && (
                    <Aviso tono={aviso.tono} titulo={aviso.titulo}>
                      {aviso.texto}
                    </Aviso>
                  )
                }
              />
            </div>
          )}
        </div>
      )}
    </Dialogo>
  );
}

const Dato = ({ termino, valor, mono }: { termino: string; valor?: string; mono?: boolean }) =>
  valor ? (
    <div>
      <dt className="text-xs text-tenue">{termino}</dt>
      <dd className={cx("mt-0.5 break-words", mono && "cifra text-xs")}>{valor}</dd>
    </div>
  ) : null;

/**
 * Exportar es un permiso, y por eso el botón solo existe para quien lo tiene.
 * Se arma en el navegador con lo que ya está en pantalla: no hay endpoint de
 * exportación y fingir uno sería inventarse contrato.
 */
function descargarCsv(movimientos: Movimiento[]) {
  const escapar = (v: unknown) => `"${String(v ?? "").replaceAll('"', '""')}"`;
  const cabecera = [
    "fecha",
    "monto",
    "contraparte",
    "cliente",
    "factura",
    "estado",
    "origen",
    "razon",
    "quien",
    "cuando",
    "banco",
    "cuenta",
    "cuenta_nombre",
    "cuenta_de_donde",
  ];
  const filas = movimientos.map((m) =>
    [
      m.fecha ?? "",
      m.montoCentavos === null ? "" : (m.montoCentavos / 100).toFixed(2),
      m.contraparte,
      m.cliente ?? "",
      m.factura ?? "",
      m.estado,
      m.origen,
      m.razon,
      m.quien ?? "",
      m.cuando ?? "",
      m.cuentaBancaria?.banco ?? "",
      m.cuentaBancaria?.cuenta ?? "",
      m.cuentaBancaria?.nombre ?? "",
      m.cuentaBancaria?.origen ?? "",
    ].map(escapar),
  );

  // BOM: sin él Excel en Windows destroza las tildes.
  const csv = "﻿" + [cabecera.map(escapar), ...filas].map((f) => f.join(";")).join("\r\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `4drando-movimientos-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * El encuentro, en pasado.
 *
 * La bandeja pregunta; esto explica lo que ya se respondió. Misma forma y mismo
 * color —azul el banco, verde tu registro, teal donde se tocaron— porque es el
 * mismo hecho visto más tarde.
 */
function EncuentroHistorico({ movimiento }: { movimiento: Movimiento }) {
  const { banco, registro } = ladosDe(movimiento);
  const ejes = ejesDe(movimiento);
  if (!ejes.length && !registro) return null;

  return (
    <div className="@container">
      <div className="grid items-stretch overflow-hidden rounded-xl border border-borde @2xl:grid-cols-[1fr_auto_1fr]">
      <Lado
        rotulo="El banco dice"
        color="dato"
        monto={banco.montoCentavos}
        nombre={banco.quien}
        pie={banco.llave ?? ""}
      />
      <Ejes ejes={ejes} vacio={!registro} />
      {registro ? (
        <Lado
          rotulo="Tu registro"
          color="exito"
          monto={registro.montoCentavos}
          nombre={registro.cliente}
          pie={registro.factura ? `Factura ${registro.factura}` : ""}
          alineadoDerecha
        />
      ) : (
        <div className="flex items-center justify-center border-t border-borde p-5 text-sm text-tenue @2xl:border-t-0 @2xl:border-l">
          Nadie lo reclamó.
        </div>
      )}
      </div>
    </div>
  );
}

/** Tope de pagos por envío: el mismo de la API. */
const POR_ENVIO = 500;

/**
 * Asignar una cuenta a todos los pagos que nadie sabe a cuál entraron
 * (SPEC-030 §6). Es el caso del negocio que registra su segunda cuenta y tiene
 * los pagos viejos sin identificar. Se deshace igual que se hace: todos vuelven
 * a «sin identificar».
 */
function AsignarCuenta({ pagos }: { pagos: Movimiento[] }) {
  const { data: libro } = usarLibro();
  const elegir = usarElegirCuenta();
  const lista = libro?.cuentasBancarias ?? [];
  const [llave, setLlave] = useState("");
  const [hechos, setHechos] = useState<string[] | null>(null);

  if (!lista.length || (!hechos && !pagos.length)) return null;

  async function enviar(claves: string[], banco: string, cuenta: string) {
    for (let i = 0; i < claves.length; i += POR_ENVIO) {
      await elegir.mutateAsync({ claves: claves.slice(i, i + POR_ENVIO), banco, cuenta });
    }
  }

  async function asignar() {
    const c = lista.find((x) => llaveDe(x) === llave);
    if (!c) return;
    const claves = pagos.map((m) => m.clave);
    await enviar(claves, c.banco, identificadorDe(c));
    setHechos(claves);
  }

  return (
    <div
      className={cx(
        "flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 text-sm",
        hechos ? "border-exito/40 bg-exito-suave/60" : "border-aviso/40 bg-aviso-suave/50",
      )}
    >
      {hechos ? (
        <>
          <span className="text-tinta">
            Listo: {hechos.length} {hechos.length === 1 ? "pago quedó" : "pagos quedaron"} con su cuenta.
          </span>
          <Boton
            tamano="sm"
            variante="fantasma"
            cargando={elegir.isPending}
            onClick={async () => {
              await enviar(hechos, "", "");
              setHechos(null);
            }}
          >
            Deshacer
          </Boton>
        </>
      ) : (
        <>
          <span className="text-tinta">
            {pagos.length} {pagos.length === 1 ? "pago" : "pagos"} sin cuenta. ¿A cuál entraron todos?
          </span>
          <select
            value={llave}
            onChange={(e) => setLlave(e.target.value)}
            aria-label="Cuenta para todos"
            className="h-9 rounded-lg border border-borde bg-superficie px-2 text-sm focus:border-marca focus:outline-none"
          >
            <option value="">Elige una…</option>
            {lista.map((c) => (
              <option key={llaveDe(c)} value={llaveDe(c)}>
                {rotuloDeCuenta(c)}
              </option>
            ))}
          </select>
          <Boton tamano="sm" variante="primario" disabled={!llave} cargando={elegir.isPending} onClick={() => void asignar()}>
            Asignar a los {pagos.length}
          </Boton>
        </>
      )}
      {elegir.error && <span className="w-full text-xs text-riesgo-tinta">{elegir.error.message}</span>}
    </div>
  );
}
